<script setup lang="ts">
import { ApiError, NetworkError } from "../lib/api"
import { createThread, listCollections, listContactable, MANY_ORGANISATIONS, sendGroupMessage, useMessaging } from "../composables/useMessaging"
import { t, tn } from "../lib/i18n"

const emit = defineEmits<{ close: []; created: [id: string] }>()
const hub = useMessaging()

const kind = ref<"organisations" | "private" | "group">("organisations")
const myOrgs = computed(() => hub.me?.organisations ?? [])
/** Shops write to their collections' members */
const myShops = computed(() => myOrgs.value.filter((o) => o.kind === "shop"))
const shopId = ref(myShops.value.find((o) => o.id === hub.orgFilter)?.id ?? myShops.value[0]?.id ?? "")
const collections = ref<{ id: string; name: string; members: number }[] | null>(null)
const collectionId = ref("")
const body = ref("")

watch(
  [kind, shopId],
  async () => {
    if (kind.value !== "group" || !shopId.value) return
    collections.value = null
    try {
      collections.value = await listCollections(shopId.value)
      collectionId.value = collections.value[0]?.id ?? ""
    } catch {
      collections.value = []
      error.value = t("Can't load the collections right now.")
    }
  },
)
// The organisation Messages shows, when it shows one
const asOrganisationId = ref(myOrgs.value.find((o) => o.id === hub.orgFilter)?.id ?? myOrgs.value[0]?.id ?? "")
const sender = computed(() => myOrgs.value.find((o) => o.id === asOrganisationId.value))
/** "Send as" among many: a search (HubOrgPicker) for the organisations, or for the shops of a group message */
const picking = ref<"organisations" | "shops" | null>(null)
const nameOf = (id: string) => myOrgs.value.find((o) => o.id === id)?.name ?? ""

function picked(id: string | null) {
  if (id && picking.value === "organisations") asOrganisationId.value = id
  else if (id && picking.value === "shops") shopId.value = id
  picking.value = null
}
/** Who the selected organisation can write to; null while loading */
const contactable = ref<{ id: string; name: string; kind: string }[] | null>(null)
const withOrganisationId = ref("")
const withEmail = ref("")
const subject = ref("")
const busy = ref(false)
const error = ref("")

watch(
  asOrganisationId,
  async (as) => {
    if (!as) return
    contactable.value = null
    try {
      const list = await listContactable(as)
      if (as !== asOrganisationId.value) return
      contactable.value = list
      withOrganisationId.value = list[0]?.id ?? ""
    } catch {
      contactable.value = []
      withOrganisationId.value = ""
      error.value = t("Can't load the list of organisations right now.")
    }
  },
  { immediate: true },
)

onMounted(() => {
  if (myOrgs.value.length === 0) kind.value = "private"
})

async function create() {
  busy.value = true
  error.value = ""
  try {
    const id =
      kind.value === "group"
        ? await sendGroupMessage({ organisationId: shopId.value, collectionId: collectionId.value, subject: subject.value.trim(), body: body.value })
        : kind.value === "organisations"
        ? await createThread({
            kind: "organisations",
            asOrganisationId: asOrganisationId.value,
            withOrganisationId: withOrganisationId.value,
            subject: subject.value.trim(),
          })
        : await createThread({ kind: "private", withEmail: withEmail.value.trim(), subject: subject.value.trim() })
    emit("created", id)
  } catch (err) {
    error.value =
      err instanceof NetworkError
        ? t("You're offline. A new conversation needs a connection.")
        : err instanceof ApiError
          ? err.message
          : t("Something went wrong.")
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <HubSheet :title="t('New conversation')" @close="emit('close')">
    <form class="form" @submit.prevent="create">
      <div v-if="myOrgs.length && (!hub.scope || myShops.length)" class="kinds" role="radiogroup" :aria-label="t('Kind of conversation')">
        <button type="button" class="kind" :class="{ on: kind === 'organisations' }" role="radio" :aria-checked="kind === 'organisations'" @click="kind = 'organisations'">
          {{ t("Between organisations") }}
        </button>
        <button v-if="!hub.scope" type="button" class="kind" :class="{ on: kind === 'private' }" role="radio" :aria-checked="kind === 'private'" @click="kind = 'private'">
          {{ t("Private message") }}
        </button>
        <button v-if="myShops.length" type="button" class="kind" :class="{ on: kind === 'group' }" role="radio" :aria-checked="kind === 'group'" @click="kind = 'group'">
          {{ t("To members") }}
        </button>
      </div>

      <template v-if="kind === 'organisations'">
        <label v-if="myOrgs.length > MANY_ORGANISATIONS">
          {{ t("Send as") }}
          <button type="button" class="hub-field choose" :aria-haspopup="true" @click="picking = 'organisations'">
            <span>{{ nameOf(asOrganisationId) }}</span>
            <HubIcon name="down" :size="20" />
          </button>
        </label>
        <label v-else-if="myOrgs.length > 1">
          {{ t("Send as") }}
          <select v-model="asOrganisationId" class="hub-field">
            <option v-for="o in myOrgs" :key="o.id" :value="o.id">{{ o.name }}</option>
          </select>
        </label>
        <label>
          {{ t("Recipient") }}
          <select v-model="withOrganisationId" class="hub-field" required :disabled="!contactable?.length">
            <option v-for="o in contactable ?? []" :key="o.id" :value="o.id">{{ o.name }}</option>
          </select>
        </label>
        <p v-if="contactable && !contactable.length && sender" class="note">{{ t("There's no one {name} can write to yet.", { name: sender.name }) }}</p>
      </template>
      <template v-else-if="kind === 'group'">
        <label v-if="myShops.length > MANY_ORGANISATIONS">
          {{ t("Send as") }}
          <button type="button" class="hub-field choose" :aria-haspopup="true" @click="picking = 'shops'">
            <span>{{ nameOf(shopId) }}</span>
            <HubIcon name="down" :size="20" />
          </button>
        </label>
        <label v-else-if="myShops.length > 1">
          {{ t("Send as") }}
          <select v-model="shopId" class="hub-field">
            <option v-for="o in myShops" :key="o.id" :value="o.id">{{ o.name }}</option>
          </select>
        </label>
        <label>
          {{ t("Members of") }}
          <select v-model="collectionId" class="hub-field" required :disabled="!collections?.length">
            <option v-for="c in collections ?? []" :key="c.id" :value="c.id">{{ tn(c.members, "{name} ({n} member)", "{name} ({n} members)", { name: c.name }) }}</option>
          </select>
        </label>
        <p v-if="collections && !collections.length" class="note">{{ t("None of the shop's collections has members yet.") }}</p>
      </template>
      <label v-else>
        {{ t("To (email)") }}
        <input v-model="withEmail" class="hub-field" type="email" required placeholder="name@example.com" />
      </label>

      <label>
        {{ t("Subject (optional)") }}
        <input v-model="subject" class="hub-field" maxlength="200" />
      </label>
      <template v-if="kind === 'group'">
        <label>
          {{ t("Message") }}
          <textarea v-model="body" class="hub-field" rows="5" required maxlength="20000" />
        </label>
        <p class="note">{{ t("Each member gets it in a conversation of their own with the shop, and their replies come to you one by one.") }}</p>
      </template>

      <p v-if="error" class="error" role="alert">{{ error }}</p>
      <div class="actions">
        <button class="hub-btn hub-btn-quiet" type="button" @click="emit('close')">{{ t("Cancel") }}</button>
        <button v-if="kind === 'group'" class="hub-btn" type="submit" :disabled="busy || !collectionId || !body.trim()">{{ t("Send to members") }}</button>
        <button v-else class="hub-btn" type="submit" :disabled="busy || (kind === 'organisations' && !withOrganisationId)">{{ t("Start the conversation") }}</button>
      </div>
    </form>
  </HubSheet>
  <HubOrgPicker
    v-if="picking"
    :title="t('Send as')"
    :organisations="picking === 'shops' ? myShops : myOrgs"
    :selected="picking === 'shops' ? shopId : asOrganisationId"
    @select="picked"
    @close="picking = null"
  />
</template>

<style scoped>
.form {
  display: grid;
  gap: 14px;
  padding: 0 8px 4px;
}

label {
  display: grid;
  gap: 6px;
  font-weight: 600;
  font-size: 0.9rem;
}

.kinds {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 4px;
  padding: 4px;
  background: var(--hub-tint);
  border-radius: 14px;
}

.kind {
  border: 0;
  background: none;
  border-radius: 10px;
  padding: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.kind.on {
  background: var(--hub-surface);
  font-weight: 700;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.12);
}

.error {
  color: var(--hub-danger);
  margin: 0;
}

.note {
  margin: 0;
  color: var(--hub-muted);
  font-size: 0.85rem;
}

.choose {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
}

.choose span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

textarea.hub-field {
  resize: vertical;
  font: inherit;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
